import type { BanDich } from '../../types';
import type { viHoi } from './hoi.vi';

export const enHoi: Pick<BanDich, keyof typeof viHoi> = {
  troLyAiHoi: {
    tieuDe: 'AI Q&A',
    moTa: 'The model turns your question into a SQL query. The query must pass the platform query guard (SELECT only, capped row count, read-only account) before it runs on the data replica. Every answer comes with the query that ran, the row count and when the data was last updated.',
    phuDe: 'Ask in Vietnamese over the data replica — answers include the SQL query and when the data was last updated.',
    doanHoiMoi: 'New chat',
    doanHoiMacDinh: 'New chat',
    chuaCoDoan: 'No chats yet. A chat is saved automatically when you send your first question.',
    lichSuTheoNguon: 'History is kept per data source and per your permissions. A chat shows up for every source selection that contains its sources.',
    doiNguonDongDoan: 'Started a new chat because the previous one belongs to the earlier source selection. Reopening it from the list restores that source selection.',
    nhieuNguonChuaSanSang: '{{n}} sources not ready',
    xoaDoanHoi: 'Delete chat {{ten}}',
    xacNhanXoaTieuDe: 'Delete chat “{{ten}}”?',
    xacNhanXoaNoiDung: 'The chat and every question in it will be deleted. This cannot be undone.',
    xoaDoanNut: 'Delete chat',
    oSoanPlaceholder: 'Ask about your data — state the period (month, quarter, year) in the question',
    cauHoi: 'Question',
    guiCauHoi: 'Send question',
    nguonChuaSanSang: 'Source not ready',
    chuaSanSang: '{{ten}} — not ready',
    llmOff: "Can't reach the model ({{model}}). You can still write SQL yourself and run it in Testing — the query guard, the data replica and the data timestamp don't depend on the model.",
    enterDeGui: 'Enter to send · Shift+Enter for a new line · questions are saved per source',
    loiNguonTieuDe: "Couldn't load the source list",
    loiNguonGoiY: 'If you signed in before your latest permission change, your session has not picked it up yet — sign out and sign in again.',
    chuaChonNguon: "No data source selected, so the question can't be sent.",
    goiYChuaCo: 'No suggested questions for this source yet.',
    chuaCoNguon: 'No data sources yet. Add one in Source connections to start asking.',
    ganDay: 'Recent',
    danhSachDoan: 'Chat list',
    nguonDuLieu: 'Data sources for the question',
    tuyChonTraLoi: 'Answer options',
    moHinh: 'Model',
    khongTaiDuocMoHinh: "Couldn't load the model list",
    chuaCauHinh: 'not configured',
    hoiBangGiongNoi: 'Ask by voice',
    dangNghe: 'Listening — click to stop',
    dungNghe: 'Stop listening',
    dung: 'Stop',
    enterDeGuiNgan: 'Enter to send · Shift+Enter for a new line',
    daLuuLuot: 'saved turn',
  },
  troLyAiHoiLuot: {
    tieuDeNhan: 'Declared labels',
    tieuDeTenCot: 'Column names',
    kieuTieuDe: 'Column headers',
    khaiCotNay: '→ Declare column {{cot}}',
    khongTraLoiDuoc: "Couldn't answer",
    chiTietKyThuat: 'Technical details (for administrators)',
    thuHoi: 'Try asking',
    hoiTiep: 'Follow up',
    chonMotCauCuThe: 'Pick a specific question',
    canLamRo: 'Needs clarification',
    quaTranDam: 'Explanation skipped',
    quaTranMo: 'because the time limit was exceeded — the table below is still the complete result and passed every check.',
    quaTranBotMo: 'because the time limit was exceeded — the result is still complete and passed every check.',
    nguonGocSo: 'Where the numbers come from',
    soTinhRa: 'Computed from the table',
    deXuat: "Suggested options — the assistant's reasoning, not data",
    taiLieuDaDan: 'Cited documents · {{n}} passages',
    doan: 'passage {{n}}',
    dangDocThanhTieng: 'Generating voice...',
    chuAnViGiongNoi: 'This bot uses the Voice channel — answers are spoken, not shown as text.',
    docHongHienChu: "Couldn't generate the full voice reading, so the text is shown so you don't miss the answer.",
    dong: '{{n}} rows',
    daCatBot: 'truncated',
    soLieuToi: 'data updated to {{luc}}',
    soLieuTrucTiep: 'live data',
    khongTraONguon: 'Not queried in {{n}} sources',
    soLieuDaCat: 'Saved {{luu}}/{{tong}} rows — the chart is not redrawn when the data is incomplete.',
    luotNhieuNguon: 'This turn queried several sources: {{ds}}. The table above belongs to the first source — the other sources’ tables were not saved.',
    nguonTomTat: '{{ten}} ({{chiTiet}})',
    phanTrang: '{{dau}}–{{cuoi}} / {{tong}} rows',
    bangKetQua: 'Result table',
    hetGio: 'The question ran longer than 120 seconds and was stopped. Try a narrower question (one period, one group) and ask again.',
  },
  troLyAiHoiTienTrinh: {
    dangBatDau: 'Starting...',
    xemTienTrinh: 'Show progress · {{n}} steps',
    anTienTrinh: 'Hide progress · {{n}} steps',
    daVietLai: 'The system reviewed and rewrote the query {{n}} times; the final version passed the safety check before running.',
    soKhongNguon: '{{n}} numbers in the answer could not be traced to a source (data cell or document passage): {{ds}} — check them against the data and documents below.',
    cauVuotBang: '{{n}} statements describe the whole table but rely only on the first rows: {{ds}}',
    thoiGianChang: 'Time per step',
    chongLan: 'This step runs in parallel with the other steps marked ∥ — do not add up their times.',
    mauDau: 'first token after {{ms}} ms',
    loiGoiMoHinh: '{{n}} model calls',
    khaiCotNay: '→ Declare column {{cot}}',
    token: '{{vao}} in · {{ra}} out',
    dem: '· cached {{pt}}%',
  },
  troLyAiHoiKichBan: {
    tienDo: 'Scenario progress {{ten}}',
    daXong: 'Done',
    buoc: 'Step {{i}}/{{n}}',
    conThieu: 'Still missing:',
  },
  troLyAiHoiGiong: {
    chuaCoGiong: 'The server has no voices yet.',
    chuaCoGiongMo: 'Voices must be configured for the Q&A service on the server — not on this machine.',
    giongDoc: 'Voice',
    batDoc: 'Read answers aloud',
    tatDoc: 'Stop reading aloud',
    tamDung: 'Pause reading',
    tiepTuc: 'Resume',
  },
  troLyAiHoiBot: {
    tieuDe: 'AI chatbot',
    phuDe: 'Ask in Vietnamese; answers draw on the data sources and documents granted to this chatbot.',
    khongCoBot: "There's no chatbot with ID {{id}}, or you haven't been granted access to it. It may have just been deleted.",
    veLuoiBot: 'Back to bot list',
    doanChatMoi: 'New chat',
    tatLuuLichSu: 'This bot has history saving turned off, so no chats are saved. Turn it on in Chatbot settings to review chats later.',
    chuaCoDoanChat: 'No chats yet.',
    doanTheoQuyen: 'Chats are private to your permissions.',
    xoaDoanChat: 'Delete chat {{ten}}',
    botVanBan: 'Text only',
    botVanBanGhi: 'This bot uses the “Text” channel, so it does not read aloud. Change it in Chatbot settings.',
    botDangTat: 'Bot is off',
    botDangTatGhi: 'The bot is off — questions still run here for testing, but users cannot chat with it yet.',
    moBotLai: 'This bot is off. Turn it on in Chatbot settings to reopen it to users.',
    hoiMotCau: 'Ask a question to start the conversation.',
    nhieuNguon: '{{n}} sources',
    nhieuNguonGhi: 'Each question is looked up separately in every attached source; the answer states which number belongs to which source.',
    thieuApiKey: 'No API key is configured for the AI model ({{model}}).',
    hoiBot: 'Ask {{ten}}...',
    botMacDinh: 'the bot',
    modeKnowledgeOnly: 'Answers only within documents',
    modeOpen: 'May answer beyond documents',
    tatCaBot: 'All bots',
  },
  troLyAiHoiTuyChon: {
    tangTraLoi: 'Analysis level',
    tangTraLoiGhi: 'What the explanation around the numbers covers. The table appears only when the explanation cannot cover everything.',
    doDai: 'Answer length',
    doDaiGhi: 'How many words. Different from the analysis level above — the level decides what gets written.',
    bieuDo: 'Chart',
    traWeb: 'Search the web',
    traWebNgan: 'Web search',
    traWebNhan: 'Search the web: {{bac}}',
  },
  troLyAiHoiDoDai: {
    ngan: { nhan: 'Short', mo: 'One paragraph — the conclusion and the numbers, nothing more.' },
    vua: { nhan: 'Medium', mo: 'Three short paragraphs. Enough for range, share and one notable point.' },
    chi_tiet: { nhan: 'Detailed', mo: 'Four paragraphs with longer sentences. Room for distribution and comparison.' },
    day_du: { nhan: 'Full', mo: 'Six paragraphs. 1–2 seconds slower because more is written.' },
  },
  troLyAiHoiCapVan: {
    gon: { nhan: 'Concise', mo: 'Only the conclusion and the key numbers' },
    phan_tich: { nhan: 'Analysis', mo: 'Adds range, share and anomalies' },
    khuyen_nghi: { nhan: 'Recommendation', mo: 'Adds scope and next steps' },
  },
  troLyAiHoiChonWeb: {
    khong: { nhan: 'No search', mo: 'Skip the web entirely — fastest answers' },
    tu_dong: { nhan: 'Automatic', mo: 'Search only when data and documents return nothing — adds 6–7 seconds when it searches' },
    chi_web: { nhan: 'Web only', mo: 'Skip data and documents, use only the declared sites' },
  },
  troLyAiHoiChonBieuDo: {
    tu_dong: { nhan: 'Automatic', mo: 'Draw when the question asks for it — “draw a chart”, “graph”...' },
    co: { nhan: 'Always draw', mo: 'Draw on every turn if the data fits a chart type' },
    khong: { nhan: 'Never draw', mo: 'Do not draw, even when the question asks' },
  },
  troLyAiHoiKenh: {
    van_ban: 'Text only',
    giong_noi: 'Voice',
    ca_hai: 'Voice + text',
  },
  troLyAiHoiLuoiBot: {
    tieuDe: 'Chat with a bot',
    phuDe: 'Pick a bot to start chatting. Each bot answers within the documents and data attached to it.',
    timBot: 'Search bots by name or description',
    demBot: '{{shown}}/{{total}} bots',
    demNguon: '{{n}} sources',
    rongTieuDe: 'No bots available',
    rongMoTa: "You haven't been granted access to any bot. Contact an administrator for access.",
    rongMoTaQuanTri: 'No bot is turned on with a source to answer from. Create or turn on a bot in Chatbot settings.',
    sangCauHinh: 'Go to Chatbot settings',
    khongKhopTieuDe: 'No bots match',
    danhSachBot: 'Bot list',
  },
  troLyAiHoiBieuDo: {
    tieuDe: 'Chart',
    an: 'Hide',
    hien: 'Show',
    doiDang: 'Change chart type',
    ty: 'B',
    trieu: 'M',
    nghin: 'K',
    dongPhanPhoi: '{{tu}} – {{den}}: {{n}} rows',
    soVoiBuocDau: '{{pt}}% of the first step',
    cot: 'Column',
    cot_ngang: 'Bar',
    cot_nhom: 'Grouped column',
    cot_chong: 'Stacked column',
    cot_chong_100: '100% stacked column',
    duong: 'Line',
    duong_nhieu: 'Multi-line',
    vung: 'Area',
    tron: 'Pie',
    vanh: 'Donut',
    phan_tan: 'Scatter',
    bong_bong: 'Bubble',
    nhiet: 'Heatmap',
    phan_phoi: 'Histogram',
    thac: 'Waterfall',
    phieu: 'Funnel',
    radar: 'Radar',
    so_lon: 'Single number',
  },
};
